import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { SAVI_IMAGES } from "../../config/saviImages";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Settings, User, Check, Loader2, BookOpen, Upload, Image as ImageIcon } from "lucide-react";
import compressImage from "../../lib/compressImage";

const SettingsTab = ({ headers }) => {
  const [siteSettings, setSiteSettings] = useState({ steve_message: "" });
  const [savingSettings, setSavingSettings] = useState(false);
  const [handbookInfo, setHandbookInfo] = useState(null);
  const [uploadingHandbook, setUploadingHandbook] = useState(false);
  const [heroConfig, setHeroConfig] = useState({ url: "" });
  const [uploadingHero, setUploadingHero] = useState(false);
  const [savingHero, setSavingHero] = useState(false);
  const heroInputRef = useRef(null);

  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const resolvedHeroUrl = heroConfig.url
    ? (heroConfig.url.startsWith("/") ? `${backend}${heroConfig.url}` : heroConfig.url)
    : SAVI_IMAGES.villaAerial;

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/admin/site-settings`, { headers });
        const settingsObj = {};
        (res.data || []).forEach(s => { settingsObj[s.key] = s.value; });
        setSiteSettings(prev => ({ ...prev, ...settingsObj }));
        // Hero config is stored as JSON inside the site_settings value
        if (settingsObj.dashboard_hero) {
          try {
            const parsed = JSON.parse(settingsObj.dashboard_hero);
            setHeroConfig({ url: parsed.url || "" });
          } catch {
            // legacy plain string → treat as URL
            setHeroConfig({ url: settingsObj.dashboard_hero });
          }
        }
      } catch (error) {
        console.error("Error fetching site settings:", error);
      }
    };
    const fetchHandbook = async () => {
      try {
        const res = await axios.get(`${API}/handbook/info`);
        setHandbookInfo(res.data);
      } catch (error) {
        console.error("Error fetching handbook info:", error);
      }
    };
    fetchSettings();
    fetchHandbook();
  }, []);

  const uploadHeroImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file");
      return;
    }
    setUploadingHero(true);
    try {
      const compressed = await compressImage(file);
      const formData = new FormData();
      formData.append("file", compressed);
      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      setHeroConfig({ url: res.data.url });
      toast.success("Photo uploaded — don't forget to Save");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingHero(false);
      if (heroInputRef.current) heroInputRef.current.value = "";
    }
  };

  const saveHero = async () => {
    if (!heroConfig.url) {
      toast.error("Upload a photo first");
      return;
    }
    setSavingHero(true);
    try {
      await axios.put(`${API}/admin/site-settings/dashboard_hero`,
        { value: JSON.stringify({ url: heroConfig.url }) },
        { headers }
      );
      toast.success("Dashboard hero updated ✨");
    } catch (error) {
      toast.error("Failed to save");
    } finally {
      setSavingHero(false);
    }
  };

  const uploadHandbook = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Handbook must be a PDF");
      return;
    }
    setUploadingHandbook(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/admin/handbook`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      toast.success("Handbook updated! Guests now see the new version.");
      setHandbookInfo({ available: true, ...res.data });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingHandbook(false);
      e.target.value = "";
    }
  };

  const saveSiteSetting = async (key, value) => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/admin/site-settings/${key}`, { value }, { headers });
      toast.success("Setting saved successfully");
      setSiteSettings(prev => ({ ...prev, [key]: value }));
    } catch (error) {
      toast.error("Failed to save setting");
      console.error("Error saving setting:", error);
    } finally {
      setSavingSettings(false);
    }
  };

  return (
          <div className="space-y-6">
            {/* Site Messages Section */}
            <div className="bg-white rounded-3xl p-6 border border-stone-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-[#3D4035] flex items-center justify-center">
                  <Settings className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-serif text-xl text-[#3D4035]">Site Settings</h3>
                  <p className="text-sm text-[#3D4035]/60">Manage site-wide content and messages</p>
                </div>
              </div>

              {/* Steve's Message */}
              <div className="bg-[#F4F1DE] rounded-2xl p-5">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-[#1A1A1A] flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-serif text-sm">S</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-[#3D4035]">Message from Steve</h4>
                    <p className="text-sm text-[#3D4035]/60">This message appears on every guest's dashboard homepage</p>
                  </div>
                </div>
                <Textarea
                  value={siteSettings.steve_message}
                  onChange={(e) => setSiteSettings({ ...siteSettings, steve_message: e.target.value })}
                  placeholder="Write a welcome message for your guests..."
                  className="min-h-[120px] rounded-xl bg-white border-stone-200 text-[#3D4035]"
                  data-testid="steve-message-input"
                />
                <div className="flex justify-end mt-4">
                  <Button
                    onClick={() => saveSiteSetting('steve_message', siteSettings.steve_message)}
                    disabled={savingSettings}
                    className="bg-[#3D4035] hover:bg-[#5D6055] rounded-full"
                    data-testid="save-steve-message-btn"
                  >
                    {savingSettings ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Save Message
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Season theme for the resident app */}
            <div className="bg-white rounded-3xl p-6 border border-stone-100" data-testid="theme-settings-card">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#F4F1DE] flex items-center justify-center flex-shrink-0 text-lg">🎨</div>
                <div>
                  <h3 className="font-medium text-[#3D4035]">Season theme (resident app)</h3>
                  <p className="text-sm text-[#3D4035]/60">Changes the colours inside the guest app for everyone, same day. The public website keeps the brand colours.</p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {[
                  { key: "classic", label: "Classic", swatch: ["#C85A38", "#8C9A7C", "#F4ECE2"] },
                  { key: "autumn", label: "Autumn", swatch: ["#B5542C", "#7E8B5E", "#E9B44C"] },
                  { key: "winter", label: "Winter", swatch: ["#2F5D50", "#9B2F3A", "#F3EEE4"] },
                  { key: "spring", label: "Spring", swatch: ["#D8745E", "#7FA37A", "#F6EFF0"] },
                  { key: "summer", label: "Summer", swatch: ["#2E6F95", "#E3A23A", "#FBF6E9"] },
                  { key: "halloween", label: "Halloween 🎃", swatch: ["#E0581B", "#6B3FA0", "#EFE6F3"] },
                ].map((th) => {
                  const active = (siteSettings.app_theme || "classic") === th.key;
                  return (
                    <button
                      key={th.key}
                      onClick={async () => { await saveSiteSetting("app_theme", th.key); window.dispatchEvent(new CustomEvent("savi-theme-changed", { detail: th.key })); }}
                      disabled={savingSettings}
                      className={`rounded-2xl border p-3 text-left transition-colors ${active ? "border-[#3D4035] bg-[#F4F1DE]" : "border-stone-200 hover:border-stone-300"}`}
                      data-testid={`theme-option-${th.key}`}
                      aria-pressed={active}
                    >
                      <div className="flex gap-1 mb-2">
                        {th.swatch.map((c) => <span key={c} className="w-5 h-5 rounded-full border border-black/5" style={{ backgroundColor: c }} />)}
                      </div>
                      <p className="text-sm font-medium text-[#3D4035]">{th.label}</p>
                      {active && <p className="text-[10px] text-[#3D4035]/60">Live now</p>}
                    </button>
                  );
                })}
              </div>
              <div className="mt-4 flex flex-wrap items-end gap-3" data-testid="theme-until-row">
                <div>
                  <label className="block text-xs text-[#3D4035]/60 mb-1" htmlFor="theme-until">Switch back to the season default after</label>
                  <input
                    id="theme-until"
                    type="date"
                    value={siteSettings.app_theme_until || ""}
                    onChange={(e) => setSiteSettings({ ...siteSettings, app_theme_until: e.target.value })}
                    className="h-9 px-3 rounded-lg border border-stone-200 text-sm bg-white"
                    data-testid="theme-until-input"
                  />
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={savingSettings}
                  onClick={async () => { await saveSiteSetting("app_theme_until", siteSettings.app_theme_until || ""); window.dispatchEvent(new CustomEvent("savi-theme-refresh")); }}
                  className="rounded-full h-9"
                  data-testid="theme-until-save"
                >
                  Save date
                </Button>
                <p className="text-[11px] text-[#3D4035]/50 basis-full">
                  {siteSettings.app_theme_until
                    ? `The ${siteSettings.app_theme || "classic"} skin runs until ${siteSettings.app_theme_until}, then the app goes back to the current season (autumn / winter / spring / summer) by itself.`
                    : "Leave empty to keep the chosen theme until you change it."}
                </p>
              </div>
            </div>

            {/* Dashboard Hero Image */}
            <div className="bg-white rounded-3xl p-6 border border-stone-100" data-testid="hero-settings-card">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#F4F1DE] flex items-center justify-center flex-shrink-0">
                  <ImageIcon className="w-5 h-5 text-[#E07A5F]" />
                </div>
                <div>
                  <h4 className="font-medium text-[#3D4035]">Dashboard hero photo</h4>
                  <p className="text-sm text-[#3D4035]/60">The big image at the top of every guest&apos;s dashboard. Upload a fresh shot any time.</p>
                </div>
              </div>

              {/* Live preview at the same height as the real dashboard */}
              <div className="relative rounded-2xl overflow-hidden shadow-sm bg-stone-100 mb-3">
                <img
                  src={resolvedHeroUrl}
                  alt="Dashboard hero preview"
                  className="w-full h-32 sm:h-44 object-cover"
                  data-testid="hero-preview-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/15 pointer-events-none" />
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <p className="text-[#F2CC8F] font-medium text-xs" style={{textShadow: '0 2px 8px rgba(0,0,0,0.95)'}}>Welcome back</p>
                  <p className="font-serif text-xl text-white" style={{textShadow: '0 2px 14px rgba(0,0,0,0.95)'}}>Marina!</p>
                </div>
              </div>

              <input
                ref={heroInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={uploadHeroImage}
                data-testid="hero-file-input"
              />
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  onClick={() => heroInputRef.current?.click()}
                  disabled={uploadingHero}
                  variant="outline"
                  className="sm:flex-1 rounded-full border-stone-200"
                  data-testid="hero-upload-btn"
                >
                  {uploadingHero ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading…</>
                  ) : (
                    <><Upload className="w-4 h-4 mr-2" /> Upload new photo</>
                  )}
                </Button>
                <Button
                  onClick={saveHero}
                  disabled={savingHero || !heroConfig.url}
                  className="sm:flex-1 bg-[#3D4035] hover:bg-[#5D6055] rounded-full"
                  data-testid="hero-save-btn"
                >
                  {savingHero ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                  ) : (
                    <><Check className="w-4 h-4 mr-2" /> Save hero</>
                  )}
                </Button>
              </div>
            </div>

            {/* Guest Handbook Card */}
            <div className="bg-white rounded-3xl p-6 border border-stone-100" data-testid="handbook-settings-card">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#F4F1DE] flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-5 h-5 text-[#E07A5F]" />
                </div>
                <div>
                  <h4 className="font-medium text-[#3D4035]">Guest Handbook</h4>
                  <p className="text-sm text-[#3D4035]/60">Guests open this from their dashboard. Upload a new PDF whenever it evolves.</p>
                </div>
              </div>
              {handbookInfo?.available ? (
                <div className="bg-[#F4F1DE] rounded-xl p-4 mb-4 text-sm">
                  <p className="font-medium text-[#3D4035] truncate">{handbookInfo.filename}</p>
                  <p className="text-xs text-[#3D4035]/60 mt-1">
                    Uploaded {new Date(handbookInfo.uploaded_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} · {(handbookInfo.size / 1024 / 1024).toFixed(1)} MB
                  </p>
                  <a
                    href={`${API}/handbook/download`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-[#E07A5F] hover:underline mt-1 inline-block"
                  >
                    View current version →
                  </a>
                </div>
              ) : (
                <p className="text-sm text-[#3D4035]/50 mb-4">No handbook uploaded yet.</p>
              )}
              <label className="block">
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={uploadHandbook}
                  className="hidden"
                  data-testid="handbook-upload-input"
                />
                <div className={`flex items-center justify-center gap-2 py-3 px-4 rounded-full text-sm font-medium cursor-pointer transition-colors ${
                  uploadingHandbook ? "bg-stone-200 text-[#3D4035]/40" : "bg-[#3D4035] text-white hover:bg-[#5D6055]"
                }`}>
                  {uploadingHandbook ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" /> Upload New Version (PDF)
                    </>
                  )}
                </div>
              </label>
            </div>

            {/* Preview Card */}
            <div className="bg-white rounded-3xl p-6 border border-stone-100">
              <h4 className="font-medium text-[#3D4035] mb-4 flex items-center gap-2">
                <User className="w-4 h-4" />
                Preview (Guest Dashboard)
              </h4>
              <div className="bg-[#FDFCF8] rounded-2xl p-5 border border-stone-100">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#1A1A1A] flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-serif text-sm">S</span>
                  </div>
                  <div>
                    <p className="font-medium text-[#1A1A1A] text-sm">A message from Steve</p>
                    <p className="text-[#1A1A1A]/60 text-sm mt-1">
                      "{siteSettings.steve_message || 'No message set yet'}"
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
  );
};

export default SettingsTab;
